import type { ErrorEvent } from '@sentry/react'

// EL-15179: errors thrown by scripts that iOS browsers and extensions inject into the page
// (Safari extensions, Chrome/Brave/DuckDuckGo/Firefox for iOS) get reported by our global
// `onerror`/`onunhandledrejection` handlers and show up as issues on whichever route the user
// happened to be on - mostly /change-password/:email/:hash (reset links open from email in
// whatever browser the user has), but also /login, /parking/*, /auth/success, ... They are not
// ours: our bundle loads from /assets/*.js, while these events either have no stack at all or
// have frames in the document URL itself (the injected script's origin).
//
// So the rule is "known signature AND no first-party frame" rather than a message match alone, and
// it isn't scoped to one route. A real bug of ours with a similar message still has a frame in
// /assets/ and is kept. `userCount` is no evidence either way here: Sentry.setUser only runs after
// login, so every pre-login route reports zero users by construction.
const FIRST_PARTY_FRAME = /\/assets\/[^/]+\.js/

// Messages only an injected script/extension can produce.
const INJECTED_SCRIPT_MESSAGES = [
  /runtime\.sendMessage/, // Safari/DuckDuckGo extension messaging - ELEVATED-LIVING-FRONTEND-PROD-1C
  /__firefox__/, // Firefox/Brave iOS reader-mode injection - -12C, -12D
  /feature named `pageContext` was not found/, // DuckDuckGo iOS bridge - -12E
  /^invalid origin$/i, // DuckDuckGo iOS bridge - -12F
]

// Generic enough to also happen in app code, so it needs proof of third-party origin: frames are
// present and none of them is ours (the injected script recurses at the document URL) - -11N, -126.
const NEEDS_THIRD_PARTY_FRAMES_MESSAGES = [/Maximum call stack size exceeded/]

// Chrome for iOS injects its own scripts that throw with a 1-2 letter minified name (`pa`, `Aa`,
// `Ba`, `Ea`, `Da` so far; the names change between Chrome releases) - -YT, -YV, -10T, -120, -11Z.
// They come either with no stack or with frames at the document URL (e.g. -YT: `/:415:45`, while
// our index.html has no inline script at all). Only trusted when the browser is Chrome iOS.
const CHROME_IOS = /CriOS/
const MINIFIED_NAME = /^[A-Za-z]{1,2}$/

const getUserAgent = (event: ErrorEvent) => event.request?.headers?.['User-Agent'] ?? ''

export const isKnownExtensionNoise = (event: ErrorEvent): boolean => {
  const exceptions = event.exception?.values ?? []
  // Sentry lists chained errors cause-first, so the thrown (outermost) one is last.
  const message = exceptions[exceptions.length - 1]?.value
  if (!message) return false

  const frames = exceptions.flatMap((exception) => exception.stacktrace?.frames ?? [])
  if (frames.some((frame) => FIRST_PARTY_FRAME.test(frame.filename ?? ''))) return false

  if (INJECTED_SCRIPT_MESSAGES.some((pattern) => pattern.test(message))) return true

  if (
    frames.length > 0 &&
    NEEDS_THIRD_PARTY_FRAMES_MESSAGES.some((pattern) => pattern.test(message))
  ) {
    return true
  }

  return CHROME_IOS.test(getUserAgent(event)) && MINIFIED_NAME.test(message)
}
